/**
 * CASL React Context and Provider
 *
 * Provides ability instance to the entire app via React Context
 */

import { type ReactNode, useContext, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { AdminAction } from '@/app/models/AdminAction';
import { createEmptyAbility, defineAbilitiesFor } from './ability';
import { AbilityContext, type AppAbility } from './ability-context';
import { type Resource } from './resources';
import { useAuth } from '../modules/auth/core/Auth';

// ============================================================================
// PROVIDER
// ============================================================================

interface AbilityProviderProps {
  children: ReactNode;
}

export function AbilityProvider({ children }: AbilityProviderProps) {
  const { currentUser } = useAuth();

  const ability = useMemo(() => {
    if (!currentUser) {
      return createEmptyAbility();
    }
    const fullAccess = currentUser.full_access === true;
    const permissions = currentUser.permissions ?? [];
    return defineAbilitiesFor(fullAccess, permissions);
  }, [currentUser]);

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access the ability instance
 */
export function useAbility(): AppAbility {
  return useContext(AbilityContext);
}

/**
 * Hook to check if user can access a permission
 */
export function useCan(permission: Resource, action: AdminAction = AdminAction.CONSULTAR): boolean {
  const ability = useAbility();
  return ability.can(action, permission);
}

/**
 * Hook to check if user cannot access a permission
 */
export function useCannot(permission: Resource, action: AdminAction = AdminAction.CONSULTAR): boolean {
  const ability = useAbility();
  return ability.cannot(action, permission);
}

/**
 * Hook to check if user has any of the given permissions
 */
export function useCanAny(permissions: Resource[], action: AdminAction = AdminAction.CONSULTAR): boolean {
  const ability = useAbility();
  return permissions.some((p) => ability.can(action, p));
}

/**
 * Hook to check if user has all of the given permissions
 */
export function useCanAll(permissions: Resource[], action: AdminAction = AdminAction.CONSULTAR): boolean {
  const ability = useAbility();
  return permissions.every((p) => ability.can(action, p));
}

// ============================================================================
// CAN COMPONENT
// ============================================================================

/**
 * CASL Can component - use for conditional rendering
 *
 * @example
 * <Can I={AdminAction.CONSULTAR} a={RESOURCES.CATALOGO_PRODUTOS}>
 *   <ProductEditButton />
 * </Can>
 */
export const Can = createContextualCan(AbilityContext.Consumer);

// ============================================================================
// EXPORTS
// ============================================================================

export { AbilityContext };
